import React, { useCallback, useState } from 'react'
import { Dialog, DialogContent } from '@material-ui/core'
import { StyledDialogTitle } from 'components/widgets'

/**
 * Returns a MaterialUI modal component
 *
 * @param title Text displayed at the top of the modal
 * @param handleClose Optional close handler; falls back to toggling the internal open state.
 */
export default function useModal(title: string, handleClose?: Function) {
  const [open, setOpen] = useState(false)

  // Stable callbacks so the returned `Modal` component's identity only changes
  // when its actual inputs (open / title / onClose) change — not on every parent
  // render. Without this, callers that re-render frequently (e.g. a row in a
  // refetching list) caused the Dialog subtree to unmount/remount each cycle,
  // producing a random flicker.
  const openModal = useCallback(() => setOpen(true), [])
  const closeModal = useCallback(() => setOpen(false), [])
  const onClose = handleClose || closeModal

  const Modal = useCallback(
    ({ children }: { children: React.ReactChild }) => (
      <Dialog
        style={{ animationFillMode: 'forwards' }}
        open={open}
        onClose={() => onClose()}
        maxWidth="sm"
        fullWidth
      >
        <StyledDialogTitle>{title}</StyledDialogTitle>
        <DialogContent>{children}</DialogContent>
      </Dialog>
    ),
    [open, title, onClose],
  )

  return { Modal, openModal, closeModal }
}